// Pure translation between the form's `CommunicationPreferencesValues` and the
// back end's section/preference/option shape (GEIS-359).
//
// The id map below is the single source of truth for the web ⇄ server preference
// id mapping. Everything else is derived from it, so adding a preference is a
// one-line change.

import {
  buildPreferenceDelta,
  type CommunicationPreferencesPatch,
  type PreferenceDeltaEntry,
  type ServerOption,
  type ServerPreference,
  type ServerSection,
} from 'common/communication-preferences.api';
import {
  CONTACT_OPTION_NAME,
  CONTACT_PREFERENCE_IDS,
  GO_PAPERLESS_FOR_ALL_PREFERENCE_ID,
  PROMOTIONS_PREFERENCE_IDS,
  TOGGLE_OPTION_NAME,
} from 'common/communication-preferences.constants';
import {
  CHANNEL_ITEMS,
  DOCUMENT_DELIVERY_ITEMS,
  type CommunicationPreferencesValues,
  type ContactField,
  type ContactInformation,
  type ToggleGroupKey,
  type ToggleGroupState,
} from './communication-preferences.types';

// Server preference ids for the contact section, keyed by web `ContactField`.
// Sourced from the shared constants so the onboarding Account Setup save writes
// the same ids (GEIS-500).
const CONTACT_SERVER_IDS: Record<ContactField, string> = CONTACT_PREFERENCE_IDS;

// Per group: the server id of the master preference + the server id for each
// child item id. Item ids are the same on both sides only by coincidence in
// `documentDelivery`; the channel groups remap (email → careEmail / promoEmail).
// Masters + promotions ids come from the shared constants (also used by the
// onboarding paperless + marketing opt-ins, GEIS-500); the care/wellness and
// document child ids are written only by this Settings screen, so they stay
// local.
type GroupServerIds = {
  master: string;
  items: Record<string, string>;
};

const GROUP_SERVER_IDS: Record<ToggleGroupKey, GroupServerIds> = {
  documentDelivery: {
    master: GO_PAPERLESS_FOR_ALL_PREFERENCE_ID,
    items: {
      eobs: 'explanationOfBenefits',
      benefitsDocuments: 'benefitsDocuments',
      premiumStatements: 'premiumStatements',
      newsletters: 'newsletters',
      letters: 'letters',
    },
  },
  careWellness: {
    master: 'receiveUpdates',
    items: {
      email: 'careEmail',
      sms: 'careSms',
      phone: 'carePhone',
    },
  },
  promotions: {
    master: PROMOTIONS_PREFERENCE_IDS.receiveUpdates,
    items: {
      email: PROMOTIONS_PREFERENCE_IDS.email,
      sms: PROMOTIONS_PREFERENCE_IDS.sms,
      phone: PROMOTIONS_PREFERENCE_IDS.phone,
    },
  },
};

const TOGGLE_GROUP_KEYS: ToggleGroupKey[] = [
  'documentDelivery',
  'careWellness',
  'promotions',
];

const CONTACT_FIELDS: ContactField[] = [
  'mobilePhone',
  'preferredPhone',
  'email',
];

// item ids per group, drawn from the shared toggle config.
const GROUP_ITEM_IDS: Record<ToggleGroupKey, string[]> = {
  documentDelivery: DOCUMENT_DELIVERY_ITEMS.map((item) => item.id),
  careWellness: CHANNEL_ITEMS.map((item) => item.id),
  promotions: CHANNEL_ITEMS.map((item) => item.id),
};

const findOption = (
  preference: ServerPreference | undefined,
  optionName: string,
): ServerOption | undefined =>
  preference?.attributes.options.find((option) => option.name === optionName);

// Case-insensitive to match geisinger-ios (CommPrefsKeys.swift lowercases before
// comparing) — a "True" from the BE must read the same on both platforms.
const parseBoolean = (value: string | undefined): boolean =>
  value?.toLowerCase() === 'true';

// ---- server → form -------------------------------------------------------

export const serverToValues = (
  sections: ServerSection[],
): CommunicationPreferencesValues => {
  // Flatten every preference into an id → preference lookup. Preference ids are
  // unique across sections per the back-end contract.
  const byId = new Map<string, ServerPreference>();
  sections.forEach((section) => {
    section.attributes.preferences.forEach((preference) => {
      byId.set(preference.id, preference);
    });
  });

  const contact: ContactInformation = {
    mobilePhone: '',
    preferredPhone: '',
    email: '',
  };
  CONTACT_FIELDS.forEach((field) => {
    const option = findOption(
      byId.get(CONTACT_SERVER_IDS[field]),
      CONTACT_OPTION_NAME,
    );
    contact[field] = option?.value ?? '';
  });

  // Closure over `byId`. The record is assembled as a literal with the three
  // fixed keys (below) so the compiler checks completeness — no `as` assertion.
  const buildGroupState = (key: ToggleGroupKey): ToggleGroupState => {
    const serverIds = GROUP_SERVER_IDS[key];

    // The master derives from its own server preference, NOT from its children.
    const masterOption = findOption(
      byId.get(serverIds.master),
      TOGGLE_OPTION_NAME,
    );

    const items: Record<string, boolean> = {};
    GROUP_ITEM_IDS[key].forEach((itemId) => {
      const option = findOption(
        byId.get(serverIds.items[itemId]),
        TOGGLE_OPTION_NAME,
      );
      items[itemId] = parseBoolean(option?.value);
    });

    return { master: parseBoolean(masterOption?.value), items };
  };

  const groups: Record<ToggleGroupKey, ToggleGroupState> = {
    documentDelivery: buildGroupState('documentDelivery'),
    careWellness: buildGroupState('careWellness'),
    promotions: buildGroupState('promotions'),
  };

  return { contact, groups };
};

// ---- form → server -------------------------------------------------------

type FlatPreference = {
  id: string;
  name: string;
  optionName: string;
  value: string;
};

// Flatten the form values into the full list of server preferences (contact +
// every group master and item). `buildPatchDelta` diffs two of these to find
// what changed; the page never PUTs the whole thing.
export const valuesToServerPreferences = (
  values: CommunicationPreferencesValues,
): FlatPreference[] => {
  const preferences: FlatPreference[] = [];

  CONTACT_FIELDS.forEach((field) => {
    const id = CONTACT_SERVER_IDS[field];
    preferences.push({
      id,
      name: id,
      optionName: CONTACT_OPTION_NAME,
      value: values.contact[field],
    });
  });

  TOGGLE_GROUP_KEYS.forEach((key) => {
    const serverIds = GROUP_SERVER_IDS[key];
    const group = values.groups[key];

    preferences.push({
      id: serverIds.master,
      name: serverIds.master,
      optionName: TOGGLE_OPTION_NAME,
      value: String(group.master),
    });

    GROUP_ITEM_IDS[key].forEach((itemId) => {
      const id = serverIds.items[itemId];
      preferences.push({
        id,
        name: id,
        optionName: TOGGLE_OPTION_NAME,
        value: String(group.items[itemId]),
      });
    });
  });

  return preferences;
};

export const buildPatchDelta = (
  initial: CommunicationPreferencesValues,
  current: CommunicationPreferencesValues,
): CommunicationPreferencesPatch => {
  const initialById = new Map(
    valuesToServerPreferences(initial).map((preference) => [
      preference.id,
      preference,
    ]),
  );

  const changed: PreferenceDeltaEntry[] = [];
  valuesToServerPreferences(current).forEach((preference) => {
    const previous = initialById.get(preference.id);
    if (!previous || previous.value !== preference.value) {
      changed.push({
        id: preference.id,
        optionName: preference.optionName,
        value: preference.value,
      });
    }
  });

  return buildPreferenceDelta(changed);
};
